import React, { useRef, useState } from "react";
import type { SxProps, Theme } from "@mui/material/styles";
import { ButtonGroup } from "@mui/material";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import MenuComponent from "./MenuComponent";
import MenuItemComponent from "./MenuItemComponent";
import IconComponent from "@CubeComponents/icons";
import { cssLengthToPx } from "@CubeComponents/utils/cssLengthToPx";

export interface SplitButtonOptionObj {
  id: string | number;
  optionDisplay: React.ReactNode;
  buttonDisplay?: React.ReactNode;
  // Callers attach arbitrary payload fields they read back in `onClick` / `onChange`.
  [key: string]: unknown;
}

export type SplitButtonOption = string | SplitButtonOptionObj;

interface SplitButtonProps {
  options: SplitButtonOption[];
  onClick: (event: React.MouseEvent<HTMLButtonElement>, selectedOption: SplitButtonOption) => void;
  onChange?: (event: React.MouseEvent<HTMLLIElement>, selectedOption: SplitButtonOption) => void;
  buttonType?: (typeof BUTTON_TYPES)[keyof typeof BUTTON_TYPES];
  loading?: boolean;
  disabled?: boolean;
  className?: string;
  sx?: SxProps<Theme>;
  popperWidth?: string | number;
  buttonLabel?: React.ReactNode;
}

const whiteFillTypes = [
  BUTTON_TYPES.GRADIENT_BUTTON,
  BUTTON_TYPES.INFO_GRADIENT_BUTTON,
  BUTTON_TYPES.ACTIVE_BUTTON,
  BUTTON_TYPES.IN_ACTIVE_BUTTON,
  BUTTON_TYPES.IN_PROGRESS_BUTTON,
];

const SplitButton = ({
  options,
  onClick,
  onChange,
  buttonType = BUTTON_TYPES.BACKGROUND_BUTTON,
  loading = false,
  disabled = false,
  className = "",
  sx = {},
  popperWidth,
  buttonLabel,
}: SplitButtonProps) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const getOptionLabel = (option: SplitButtonOption, forButton = false) => {
    if (typeof option === "string") return option;
    return forButton ? option.buttonDisplay || option.optionDisplay : option.optionDisplay;
  };

  const handleMainClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (onClick) {
      onClick(event, options[selectedIndex]!);
    }
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleMenuItemClick = (event: React.MouseEvent<HTMLLIElement>, index: number) => {
    setSelectedIndex(index);
    setOpen(false);
    if (onChange) {
      onChange(event, options[index]!);
    }
  };

  const getWidthValue = (): number | undefined => {
    if (popperWidth !== undefined) {
      const px = cssLengthToPx(popperWidth);
      return Number.isFinite(px) ? px : undefined;
    }
    if (buttonLabel) return undefined;
    return anchorRef.current?.clientWidth ?? undefined;
  };

  return (
    <React.Fragment>
      <ButtonGroup
        variant="contained"
        ref={anchorRef}
        aria-label="split button"
        disabled={disabled}
        className={className}
        sx={{
          boxShadow: "none",
          "& .MuiButtonGroup-grouped:not(:last-of-type)": {
            borderInlineEnd: "1px solid rgba(255, 255, 255, 0.23)",
            borderColor: buttonType === BUTTON_TYPES.BORDER_BUTTON ? "#4592B7" : "rgba(255, 255, 255, 0.23)",
          },
          ...sx,
        }}
      >
        <ButtonComponent
          buttonType={buttonType}
          disabled={disabled}
          loading={loading}
          onClick={handleMainClick}
          sx={{
            marginInlineEnd: "0.25rem",
          }}
        >
          {buttonLabel ?? getOptionLabel(options[selectedIndex]!, true)}
        </ButtonComponent>

        <ButtonComponent
          buttonType={buttonType}
          disabled={disabled || loading}
          iconButton={true}
          aria-controls={open ? "split-button-menu" : undefined}
          aria-expanded={open ? "true" : undefined}
          aria-label="select option"
          aria-haspopup="menu"
          onClick={handleToggle}
          sx={{
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
            padding: "0px",
            minWidth: "1.875rem",
            margin: 0,
          }}
        >
          <IconComponent
            name={whiteFillTypes.includes(buttonType) ? "DropDownWhite" : "DropDownRegular"}
            width="2rem"
            height="2rem"
          />
        </ButtonComponent>
      </ButtonGroup>

      <MenuComponent
        anchorEl={anchorRef.current}
        open={open}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        width={getWidthValue()}
        paperSx={{
          marginTop: "4px",
          minWidth: anchorRef.current ? anchorRef.current.clientWidth : undefined,
        }}
      >
        {options.map((option: SplitButtonOption, index: number) => (
          <MenuItemComponent
            key={typeof option === "string" ? option : option.id || index.toString()}
            selected={index === selectedIndex}
            onClick={(event) => handleMenuItemClick(event, index)}
          >
            {getOptionLabel(option, false)}
          </MenuItemComponent>
        ))}
      </MenuComponent>
    </React.Fragment>
  );
};

export default SplitButton;
